import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { jsPDF } from 'jspdf';
import {
  blobToDataUrl,
  captureCompositionViaChromium,
  downloadBlobAsFile,
  preferChromiumExport,
} from './mediaCreatorChromiumExport';
import { captureArtboardCanvas } from './mediaCreatorExport';
import { renderCompositionCanvas } from './mediaCreatorForeignObjectExport';
import Button from '../../ui/Button';
import MediaCreatorHtmlArtboard from './MediaCreatorHtmlArtboard';
import MediaAssetCarousel from './MediaAssetCarousel';
import MediaAssetPickModal from './MediaAssetPickModal';
import MediaCreatorPropertiesPanel from './MediaCreatorPropertiesPanel';
import MediaCreatorLayersTree from './MediaCreatorLayersTree';
import { buildDomRegistry } from './mediaCreatorDomRegistry';
import { buildAiCopyBundle, formatAiCopyBundle } from './mediaCreatorAiCopy';
import { downloadMediaCreatorHtml } from './mediaCreatorHtmlDownload';
import { PRESET_LABELS } from './presets';
import { focalPointToObjectPosition, withSlotZoom } from './mediaCreatorPhotoFit';
import { assetUrlEntry } from './mediaCreatorHtmlResolve';
import { documentDesignService } from '../../../services/documentDesignService';
import { stripPhotoSlotLabelsFromCss } from './mediaCreatorExportCss';
import { captureLayoutSnapshot, layoutViewerFingerprint } from './mediaCreatorHtmlSync';
import MediaCreatorExportDiagnosticPanel from './MediaCreatorExportDiagnosticPanel';
import MediaCreatorFullscreenViewer from './MediaCreatorFullscreenViewer';
import { EMPTY_UNDO, isTypingTarget, recordUndo } from './mediaCreatorUndo';

const STAGE_PADDING_PX = 24;
const ZOOM_STEPS = [50, 75, 100, 125];

const MediaCreatorEditor = ({ composition, onBack, onSaved }) => {
  const [data, setData] = useState(composition);
  const [selectedElementKey, setSelectedElementKey] = useState(null);
  const [photoSlotTool, setPhotoSlotTool] = useState('crop');
  const [busy, setBusy] = useState(null);
  const [copyAiState, setCopyAiState] = useState('idle');
  const [error, setError] = useState(null);
  const [exportDiagnostic, setExportDiagnostic] = useState(null);
  const [pickTarget, setPickTarget] = useState(null);
  const [selectedPhotoSlot, setSelectedPhotoSlot] = useState(null);
  const [extraAssetUrls, setExtraAssetUrls] = useState({});
  const artRef = useRef(null);
  const stageRef = useRef(null);
  const autoBindRef = useRef(false);
  const savedFingerprintRef = useRef(layoutViewerFingerprint(composition?.layout || {}));
  const [stageWidth, setStageWidth] = useState(960);
  const [previewZoom, setPreviewZoom] = useState(100);
  const [fullscreenLayout, setFullscreenLayout] = useState(null);
  const [showSlotCaptions, setShowSlotCaptions] = useState(false);

  const assetUrls = useMemo(
    () => ({ ...(data.asset_urls || {}), ...extraAssetUrls }),
    [data.asset_urls, extraAssetUrls],
  );

  const layout = data.layout || {};
  const registryEntries = useMemo(() => {
    if (!layout.document_html) return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(layout.document_html, 'text/html');
    const root = doc.querySelector('.mc-invite') || doc.body;
    return buildDomRegistry(root, { photoSlots: layout.photo_slots });
  }, [layout.document_html, layout.photo_slots]);
  const hasHtmlDocument = layout.render_mode === 'html' || Boolean(layout.document_html);
  const retiredLayerLayout = !hasHtmlDocument;
  const layoutWidth = layout.size_px?.width || 1200;
  const fitScale = useMemo(() => {
    const inner = Math.max(320, stageWidth - STAGE_PADDING_PX);
    return inner / layoutWidth;
  }, [stageWidth, layoutWidth]);
  const scale = fitScale * (previewZoom / 100);

  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w && w > 0) setStageWidth(w);
    });
    ro.observe(el);
    setStageWidth(el.clientWidth || 960);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    autoBindRef.current = false;
  }, [data.id]);

  const dataRef = useRef(data);
  dataRef.current = data;
  const undoRef = useRef(EMPTY_UNDO);
  const [canUndo, setCanUndo] = useState(false);
  const [selectedFontSizePx, setSelectedFontSizePx] = useState(null);

  const rememberUndo = useCallback((prevLayout, source) => {
    if (!source || !prevLayout) return;
    undoRef.current = recordUndo(undoRef.current, prevLayout, source);
    setCanUndo(true);
  }, []);

  useEffect(() => {
    setData(composition);
    savedFingerprintRef.current = layoutViewerFingerprint(composition?.layout || {});
    undoRef.current = EMPTY_UNDO;
    setCanUndo(false);
  }, [composition.id]);

  /** `source` marks a user edit (undoable); automatic patches omit it. */
  const patchLayout = useCallback((updater, source) => {
    if (source) rememberUndo(dataRef.current?.layout, source);
    setData((prev) => {
      const nextLayout = typeof updater === 'function' ? updater(prev.layout) : updater;
      return { ...prev, layout: nextLayout };
    });
  }, [rememberUndo]);

  const undo = useCallback(() => {
    const { snapshot } = undoRef.current;
    if (!snapshot) return;
    undoRef.current = EMPTY_UNDO;
    setCanUndo(false);
    setData((prev) => ({ ...prev, layout: snapshot }));
  }, []);

  useEffect(() => {
    const key = selectedElementKey && !selectedElementKey.includes(':') ? selectedElementKey : null;
    if (!key) {
      setSelectedFontSizePx(null);
      return undefined;
    }
    const frame = window.requestAnimationFrame(() => {
      const el = artRef.current?.querySelector(`.mc-html-stage-inner [data-editable="${CSS.escape(key)}"]`);
      const px = el ? parseFloat(window.getComputedStyle(el).fontSize) : NaN;
      setSelectedFontSizePx(Number.isFinite(px) ? Math.round(px * 10) / 10 : null);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [selectedElementKey, data.layout?.document_css, data.layout?.document_html]);

  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey || e.key.toLowerCase() !== 'z') return;
      if (isTypingTarget(document.activeElement) || !undoRef.current.snapshot) return;
      e.preventDefault();
      undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo]);

  useEffect(() => {
    if (!hasHtmlDocument) return;
    const css = layout.document_css || '';
    if (!css || !/\.mc-photo[-\w]*\s*:\s*before/i.test(css)) return;
    const cleaned = stripPhotoSlotLabelsFromCss(css);
    if (cleaned === css) return;
    patchLayout((l) => ({ ...l, document_css: cleaned }));
  }, [data.id, hasHtmlDocument, layout.document_css, patchLayout]);

  useEffect(() => {
    if (!hasHtmlDocument || autoBindRef.current) return undefined;
    const queries = (layout.meta?.photo_queries || []).filter(Boolean);
    const slots = layout.photo_slots || [];
    const openSlots = slots.filter((s) => !layout.photo_bindings?.[s.id]);
    if (!openSlots.length) return undefined;

    const queryPool = [
      ...queries,
      'Yukon mine site aerial Minto',
      'exploration drill rig Yukon',
      'drill core tray Yukon',
      'geology team core shack',
    ];

    let cancelled = false;

    const registerCandidate = (assetId, asset) => {
      const entry = assetId ? assetUrlEntry(asset) : null;
      if (entry) setExtraAssetUrls((prev) => ({ ...prev, [assetId]: entry }));
    };

    const tryBindSlot = async (slot, used, qList) => {
      for (const q of qList) {
        if (!q || cancelled) return null;
        const rows = await documentDesignService.getCompositionAssetCandidates({
          role: 'photo',
          preset: data.preset,
          q,
          limit: 8,
        });
        const pick = rows.find((r) => {
          const id = r.media_asset_id || r.id;
          return id && !used.has(id);
        });
        if (pick) {
          const assetId = pick.media_asset_id || pick.id;
          used.add(assetId);
          registerCandidate(assetId, pick);
          const op = focalPointToObjectPosition(pick.focal_point);
          patchLayout((l) => ({
            ...l,
            photo_bindings: { ...(l.photo_bindings || {}), [slot.id]: assetId },
            photo_object_positions: { ...(l.photo_object_positions || {}), [slot.id]: op },
          }));
          return assetId;
        }
      }
      return null;
    };

    (async () => {
      const used = new Set(Object.values(layout.photo_bindings || {}));
      try {
        for (const slot of openSlots) {
          if (cancelled) return;
          const primary = [
            slot.brand_query,
            ...queries,
            ...queryPool,
          ].filter(Boolean);
          const seen = new Set();
          const qList = primary.filter((q) => {
            if (seen.has(q)) return false;
            seen.add(q);
            return true;
          });
          await tryBindSlot(slot, used, qList);
        }
      } catch (err) {
        console.warn('Media Creator: auto-bind photos failed', err);
      } finally {
        autoBindRef.current = true;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    data.id,
    data.preset,
    hasHtmlDocument,
    layout.meta?.photo_queries,
    layout.photo_bindings,
    layout.photo_slots,
    patchLayout,
  ]);

  const copyAiJson = async () => {
    const text = formatAiCopyBundle(buildAiCopyBundle(layout, data.brief || layout.brief));
    try {
      await navigator.clipboard.writeText(text);
      setCopyAiState('copied');
      window.setTimeout(() => setCopyAiState('idle'), 2000);
    } catch {
      setCopyAiState('failed');
      window.setTimeout(() => setCopyAiState('idle'), 2500);
    }
  };

  const refineWithAi = async (payload) => {
    setBusy('refine');
    setError(null);
    try {
      const layoutSnapshot = layoutForPersist();
      const updated = await documentDesignService.refineComposition(data.id, {
        ...payload,
        layout: layoutSnapshot,
        brief: data.brief || layout.brief,
        include_brief: true,
        tone: 'external',
        require_approved_photos: true,
      });
      rememberUndo(layoutSnapshot, `ai:${Date.now()}`);
      setData(updated);
      savedFingerprintRef.current = layoutViewerFingerprint(updated.layout || layoutSnapshot);
      onSaved?.(updated);
    } catch (err) {
      setError(err.message || 'AI refine failed');
    } finally {
      setBusy(null);
    }
  };

  const rerunAi = async () => {
    const briefText = (data.brief || layout.brief || '').trim();
    if (briefText.length < 8) {
      setError('Brief must be at least 8 characters to re-run AI.');
      return;
    }
    setBusy('ai');
    setError(null);
    try {
      const before = layoutForPersist();
      const updated = await documentDesignService.regenerateComposition(data.id, {
        brief: briefText,
        tone: 'external',
        require_approved_photos: true,
      });
      rememberUndo(before, `ai:${Date.now()}`);
      setData(updated);
      onSaved?.(updated);
    } catch (err) {
      setError(err.message || 'Re-run AI failed');
    } finally {
      setBusy(null);
    }
  };

  const layoutForPersist = useCallback(() => {
    if (!hasHtmlDocument) return data.layout || {};
    return captureLayoutSnapshot(artRef.current, data.layout || {});
  }, [data.layout, hasHtmlDocument]);

  const viewerDirty = useMemo(() => {
    if (!hasHtmlDocument || !artRef.current) return false;
    const snap = captureLayoutSnapshot(artRef.current, data.layout || {});
    return layoutViewerFingerprint(snap) !== savedFingerprintRef.current;
  }, [data.layout, hasHtmlDocument, previewZoom, selectedElementKey]);

  const save = async () => {
    setBusy('save');
    setError(null);
    try {
      const layoutPayload = layoutForPersist();
      setData((prev) => ({ ...prev, layout: layoutPayload }));
      const saved = await documentDesignService.updateComposition(data.id, {
        title: data.title,
        layout: layoutPayload,
        brief: data.brief,
        status: 'draft',
      });
      setData(saved);
      savedFingerprintRef.current = layoutViewerFingerprint(saved.layout || layoutPayload);
      onSaved?.(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const closeFullscreen = useCallback(() => setFullscreenLayout(null), []);

  const renderExportCanvas = async (exportLayout, backgroundColor) => {
    try {
      const { canvas, diagnostics } = await renderCompositionCanvas({
        layout: exportLayout,
        assetUrls,
        scale: 2,
        backgroundColor,
        liveArtboard: artRef.current,
      });
      return { canvas, diagnostics, restore: () => {} };
    } catch (foErr) {
      console.warn('Media Creator: browser render export failed, using html2canvas fallback', foErr);
      const res = await captureArtboardCanvas(artRef.current, {
        scale: 2,
        backgroundColor,
        layout: exportLayout,
        assetUrls,
      });
      const diagnostics = res.diagnostics
        ? {
          ...res.diagnostics,
          summary: {
            ...res.diagnostics.summary,
            exportMode: 'html2canvas_fallback',
            fallbackReason: foErr?.message || String(foErr),
          },
        }
        : null;
      return { ...res, diagnostics };
    }
  };

  const exportPng = async () => {
    if (!artRef.current) return;
    setBusy('export-png');
    setExportDiagnostic(null);
    let restore = () => {};
    const exportLayout = layoutForPersist();
    const fileStem = (data.title || 'media').replace(/[^\w\-]+/g, '_');
    try {
      if (preferChromiumExport()) {
        try {
          const blob = await captureCompositionViaChromium({
            compositionId: data.id,
            layout: exportLayout,
            assetUrls,
            title: data.title,
            scale: 2,
          });
          downloadBlobAsFile(blob, `${fileStem}.png`);
          setExportDiagnostic(null);
          await documentDesignService.updateComposition(data.id, {
            status: 'exported',
            layout: exportLayout,
          });
          return;
        } catch (chromiumErr) {
          console.warn('Media Creator: Chromium export failed, using canvas fallback', chromiumErr);
          setError(
            `${chromiumErr.message || 'Server export failed'} — trying canvas fallback…`,
          );
        }
      }
      const { canvas, restore: undoInline, diagnostics } = await renderExportCanvas(
        exportLayout,
        null,
      );
      restore = undoInline;
      const link = document.createElement('a');
      link.download = `${fileStem}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      setExportDiagnostic(diagnostics || null);
      setError(null);
      await documentDesignService.updateComposition(data.id, {
        status: 'exported',
        layout: exportLayout,
      });
    } catch (err) {
      setError(err.message || 'Export failed');
      if (err.exportDiagnostics) setExportDiagnostic(err.exportDiagnostics);
    } finally {
      restore();
      setBusy(null);
    }
  };

  const exportHtml = () => {
    try {
      downloadMediaCreatorHtml({
        layout: layoutForPersist(),
        assetUrls,
        title: data.title,
      });
    } catch (err) {
      setError(err.message || 'HTML download failed');
    }
  };

  const exportPdf = async () => {
    if (!artRef.current) return;
    setBusy('export-pdf');
    setExportDiagnostic(null);
    let restore = () => {};
    const exportLayout = layoutForPersist();
    const w = exportLayout.size_px?.width || 1200;
    const h = exportLayout.size_px?.height || 630;
    const fileStem = (data.title || 'media').replace(/[^\w\-]+/g, '_');
    try {
      let imgDataUrl;
      if (preferChromiumExport()) {
        try {
          const blob = await captureCompositionViaChromium({
            compositionId: data.id,
            layout: exportLayout,
            assetUrls,
            title: data.title,
            scale: 2,
          });
          imgDataUrl = await blobToDataUrl(blob);
        } catch (chromiumErr) {
          console.warn('Media Creator: Chromium export failed, using canvas fallback', chromiumErr);
          setError(
            `${chromiumErr.message || 'Server export failed'} — trying canvas fallback…`,
          );
          imgDataUrl = null;
        }
      }
      if (!imgDataUrl) {
        const { canvas, restore: undoInline, diagnostics } = await renderExportCanvas(
          exportLayout,
          '#ffffff',
        );
        restore = undoInline;
        imgDataUrl = canvas.toDataURL('image/png');
        setExportDiagnostic(diagnostics || null);
      }
      setError(null);
      const pdf = new jsPDF({
        orientation: w > h ? 'landscape' : 'portrait',
        unit: 'px',
        format: [w, h],
      });
      pdf.addImage(imgDataUrl, 'PNG', 0, 0, w, h);
      pdf.save(`${fileStem}.pdf`);
    } catch (err) {
      setError(err.message || 'PDF export failed');
      if (err.exportDiagnostics) setExportDiagnostic(err.exportDiagnostics);
    } finally {
      restore();
      setBusy(null);
    }
  };

  const meta = layout.meta || {};
  const suggestedPhotos = meta.suggested_photos || [];
  const suggestedLogos = meta.suggested_logos || [];

  const registerAsset = (assetId, asset) => {
    if (assetId == null) return;
    const entry = assetUrlEntry(asset);
    if (!entry) {
      documentDesignService
        .getMedia(assetId)
        .then((detail) => {
          const fetched = assetUrlEntry(detail);
          if (fetched) setExtraAssetUrls((prev) => ({ ...prev, [assetId]: fetched }));
        })
        .catch((err) => console.warn('Media Creator: could not load asset', assetId, err));
      return;
    }
    setExtraAssetUrls((prev) => ({ ...prev, [assetId]: entry }));
  };

  const applyPhotoSlot = (slotId, assetId, asset) => {
    registerAsset(assetId, asset);
    const op = focalPointToObjectPosition(asset?.focal_point);
    patchLayout((l) => ({
      ...l,
      photo_bindings: { ...(l.photo_bindings || {}), [slotId]: assetId },
      photo_object_positions: { ...(l.photo_object_positions || {}), [slotId]: op },
      photo_zoom: withSlotZoom(l.photo_zoom, slotId, 1),
    }), `photo:${slotId}`);
    setPickTarget(null);
    setSelectedPhotoSlot(slotId);
  };

  const applyLogo = (assetId, asset) => {
    registerAsset(assetId, asset);
    patchLayout((l) => ({ ...l, logo_binding: assetId }), 'logo');
    setPickTarget(null);
    setSelectedPhotoSlot(null);
    setSelectedElementKey('logo:brand');
  };

  const applyAsset = (assetId, asset) => {
    if (pickTarget === 'logo') {
      applyLogo(assetId, asset);
      return;
    }
    registerAsset(assetId, asset);
    if (pickTarget && String(pickTarget).startsWith('photo_')) {
      applyPhotoSlot(pickTarget, assetId, asset);
    }
  };

  const onPhotoObjectPositionChange = (slotId, objectPosition) => {
    patchLayout((l) => ({
      ...l,
      photo_object_positions: { ...(l.photo_object_positions || {}), [slotId]: objectPosition },
    }), `crop:${slotId}`);
  };

  const onPhotoZoomChange = (slotId, zoom) => {
    patchLayout((l) => ({ ...l, photo_zoom: withSlotZoom(l.photo_zoom, slotId, zoom) }), `zoom:${slotId}`);
  };

  const onElementLayoutPatch = (key, transform) => {
    patchLayout((l) => ({
      ...l,
      element_layout: { ...(l.element_layout || {}), [key]: transform },
    }), `layout:${key}`);
  };

  const resetElementLayout = () => {
    if (!selectedElementKey) return;
    patchLayout((l) => {
      const next = { ...(l.element_layout || {}) };
      delete next[selectedElementKey];
      return { ...l, element_layout: next };
    }, `reset:${selectedElementKey}`);
  };

  const deleteSelectedElement = (key) => {
    const targetKey = key || selectedElementKey;
    if (!targetKey) return;
    if (targetKey.startsWith('slot:')) {
      const slotId = targetKey.slice(5);
      patchLayout((l) => {
        const bindings = { ...(l.photo_bindings || {}) };
        const positions = { ...(l.photo_object_positions || {}) };
        delete bindings[slotId];
        delete positions[slotId];
        return {
          ...l,
          photo_bindings: bindings,
          photo_object_positions: positions,
          photo_zoom: withSlotZoom(l.photo_zoom, slotId, 1),
        };
      }, `delete:${targetKey}`);
      setSelectedElementKey(null);
      setSelectedPhotoSlot(null);
      return;
    }
    patchLayout((l) => {
      const html = l.document_html || '';
      if (!html) return l;
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const root = doc.querySelector('.mc-invite');
      if (!root) return l;
      let el = root.querySelector(`[data-editable="${CSS.escape(targetKey)}"]`);
      if (!el && targetKey === 'block:badges') el = root.querySelector('.mc-badges');
      if (!el && targetKey === 'block:phase-badges') el = root.querySelector('.mc-phase-badges');
      if (!el && targetKey === 'region:invite') el = root.querySelector('.mc-invite');
      if (!el && targetKey === 'region:copy') el = root.querySelector('.mc-copy');
      if (!el && targetKey === 'region:gallery') el = root.querySelector('.mc-gallery');
      if (!el && targetKey.startsWith('figure:')) {
        const sid = targetKey.slice(7);
        el = root.querySelector(`img[data-photo-slot="${CSS.escape(sid)}"]`)?.closest('figure');
      }
      if (!el && targetKey === 'logo:brand') {
        el = root.querySelector('img[data-logo-slot]')
          || root.querySelector('img.mc-brand:not([data-photo-slot])')
          || root.querySelector('.mc-logo-wrap img:not([data-photo-slot])');
      }
      if (!el) return l;
      el.remove();
      const nextLayout = { ...l, document_html: root.outerHTML };
      const nextEl = { ...(l.element_layout || {}) };
      delete nextEl[targetKey];
      nextLayout.element_layout = nextEl;
      return nextLayout;
    }, `delete:${targetKey}`);
    setSelectedElementKey(null);
  };

  return (
    <div className="mc-editor">
      <div className="mc-editor-bar">
        <Button size="sm" variant="ghost" onClick={onBack}>← Gallery</Button>
        <input
          className="mc-title-input"
          value={data.title || ''}
          onChange={(e) => setData({ ...data, title: e.target.value })}
        />
        <span className="dd-muted">{PRESET_LABELS[data.preset] || data.preset}</span>
        <div className="mc-preview-zoom" role="group" aria-label="Preview zoom">
          {ZOOM_STEPS.map((pct) => (
            <button
              key={pct}
              type="button"
              className={`mc-zoom-btn${previewZoom === pct ? ' on' : ''}`}
              onClick={() => setPreviewZoom(pct)}
            >
              {pct}%
            </button>
          ))}
          <button
            type="button"
            className={`mc-zoom-btn${previewZoom === 100 ? ' on' : ''}`}
            onClick={() => setPreviewZoom(100)}
            title="Fit artboard to preview area"
          >
            Fit
          </button>
        </div>
        {hasHtmlDocument && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setFullscreenLayout(layoutForPersist())}
            title="View artboard fullscreen"
          >
            Fullscreen
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          disabled={!canUndo}
          onClick={undo}
          title="Undo the last change (Ctrl+Z)"
        >
          Undo
        </Button>
        <Button size="sm" variant="secondary" loading={busy === 'save'} onClick={save}>Save</Button>
        <Button size="sm" variant="secondary" onClick={copyAiJson} title="Copy prompts, model JSON, and normalized intent">
          {copyAiState === 'copied' ? 'Copied AI JSON' : copyAiState === 'failed' ? 'Copy failed' : 'Copy AI JSON'}
        </Button>
        {hasHtmlDocument && (
          <Button size="sm" variant="secondary" onClick={exportHtml} title="Standalone HTML with model CSS and resolved Brand Library URLs">
            Download HTML
          </Button>
        )}
        <Button size="sm" loading={busy === 'export-png'} onClick={exportPng}>Download PNG</Button>
        <Button size="sm" variant="secondary" loading={busy === 'export-pdf'} onClick={exportPdf}>Download PDF</Button>
      </div>
      {error && <div className="dd-error-inline">{error}</div>}
      {viewerDirty && (
        <div className="dd-error-inline mc-viewer-dirty-hint">
          Export and AI refine use the current artboard. Save to persist changes to the gallery.
        </div>
      )}
      <MediaCreatorExportDiagnosticPanel
        diagnostic={exportDiagnostic}
        onDismiss={() => setExportDiagnostic(null)}
      />
      <MediaCreatorFullscreenViewer
        open={Boolean(fullscreenLayout) && hasHtmlDocument}
        onClose={closeFullscreen}
        layout={fullscreenLayout}
        assetUrls={assetUrls}
      />
      {retiredLayerLayout && (
        <div className="dd-error-inline mc-retired-layout">
          This design uses a retired layer format. Re-run AI generation from an updated brief to edit it as HTML.
        </div>
      )}

      <div className="mc-editor-body">
        <aside className="mc-layers">
          <h4>Outline</h4>
          {hasHtmlDocument ? (
            <>
              <MediaCreatorLayersTree
                entries={registryEntries}
                selectedElementKey={selectedElementKey}
                selectedPhotoSlot={selectedPhotoSlot}
                onSelectKey={(key) => {
                  setSelectedElementKey(key);
                  setPickTarget(null);
                  if (key?.startsWith('slot:') && photoSlotTool === 'move') {
                    setSelectedElementKey(key);
                  } else if (!key?.startsWith('slot:')) {
                    setSelectedPhotoSlot(null);
                  }
                }}
                onSelectPhotoSlot={(slot) => {
                  setSelectedPhotoSlot(slot);
                  if (photoSlotTool === 'move') setSelectedElementKey(`slot:${slot}`);
                }}
              />
            </>
          ) : null}
          {hasHtmlDocument && (
            <p className="dd-muted mc-html-hint">
              Click any element to select. Edit copy on the artboard. AI refine targets your
              selection by default. Right-drag pans photo crop.
            </p>
          )}
        </aside>

        <div className="mc-stage" ref={stageRef}>
          {hasHtmlDocument ? (
            <MediaCreatorHtmlArtboard
              ref={artRef}
              layout={layout}
              assetUrls={assetUrls}
              scale={scale}
              selectedPhotoSlot={selectedPhotoSlot}
              selectedElementKey={selectedElementKey}
              photoSlotTool={photoSlotTool}
              showSlotCaptions={showSlotCaptions}
              onSelectPhotoSlot={(slot) => {
                setSelectedPhotoSlot(slot);
                setPickTarget(null);
                if (photoSlotTool === 'move') {
                  setSelectedElementKey(`slot:${slot}`);
                } else {
                  setSelectedElementKey(null);
                }
              }}
              onSelectElementKey={(key) => {
                setSelectedElementKey(key);
                if (key === 'logo:brand') {
                  setSelectedPhotoSlot(null);
                  setPickTarget(null);
                }
              }}
              onPhotoObjectPositionChange={onPhotoObjectPositionChange}
              onPhotoZoomChange={onPhotoZoomChange}
              onElementLayoutChange={onElementLayoutPatch}
              onDeleteElement={deleteSelectedElement}
              onDocumentHtmlChange={(html, info) => {
                patchLayout((l) => ({ ...l, document_html: html }), info?.auto ? undefined : 'html');
              }}
            />
          ) : (
            <p className="dd-muted">No HTML document to preview.</p>
          )}
        </div>

        {hasHtmlDocument ? (
          <MediaCreatorPropertiesPanel
            layout={layout}
            brief={data.brief || layout.brief}
            data={data}
            selectedElementKey={selectedElementKey}
            selectedPhotoSlot={selectedPhotoSlot}
            photoSlotTool={photoSlotTool}
            setPhotoSlotTool={(mode) => {
              setPhotoSlotTool(mode);
              if (mode === 'move' && selectedPhotoSlot) {
                setSelectedElementKey(`slot:${selectedPhotoSlot}`);
              } else if (mode === 'crop') {
                setSelectedElementKey(null);
              }
            }}
            registryEntries={registryEntries}
            pickTarget={pickTarget}
            setPickTarget={setPickTarget}
            meta={meta}
            suggestedLogos={suggestedLogos}
            suggestedPhotos={suggestedPhotos}
            onResetElementLayout={resetElementLayout}
            onDeleteElement={deleteSelectedElement}
            onPatchLayout={patchLayout}
            onApplyPhotoSlot={applyPhotoSlot}
            onApplyLogo={applyLogo}
            onPhotoZoomChange={onPhotoZoomChange}
            onRefine={refineWithAi}
            refineBusy={busy === 'refine'}
            onRegenerate={rerunAi}
            regenerateBusy={busy === 'ai'}
            showSlotCaptions={showSlotCaptions}
            onShowSlotCaptionsChange={setShowSlotCaptions}
            selectedFontSizePx={selectedFontSizePx}
          />
        ) : (
          <aside className="mc-props">
            <h4>Properties</h4>
            <p className="dd-muted">No HTML document.</p>
          </aside>
        )}
      </div>

      {pickTarget && (
        <MediaAssetPickModal
          title={pickTarget === 'logo' ? 'Replace brand image' : 'Photo'}
          role={pickTarget === 'logo' ? 'logo' : 'photo'}
          preset={data.preset}
          initialQuery={
            pickTarget === 'logo'
              ? ''
              : (layout.photo_slots || []).find((s) => s.id === pickTarget)?.brand_query || ''
          }
          searchPlaceholder={
            pickTarget === 'logo' ? (meta.logo_query || 'Search logos and SVG…') : undefined
          }
          onClose={() => setPickTarget(null)}
          onPick={applyAsset}
        />
      )}
    </div>
  );
};

export default MediaCreatorEditor;
